import apiClient from '@/shared/api/client';
import type {
  AgentTaskWithdrawalKind,
  AgentAssignment,
  AgentDayOneReport,
  AgentFact,
  AgentMessage,
  AgentMissionSnapshot,
  AgentTask,
  TaskVersions,
} from '../types';

export const agentsApi = {
  listTasks: (locationId: string): Promise<AgentTask[]> =>
    apiClient
      .get<{ success: boolean; data: AgentTask[] }>('/agents/tasks', { params: { locationId } })
      .then((r) => r.data.data),

  /** The location's day-one report, or null when the agent has not written it yet. */
  getDayOneReport: (locationId: string): Promise<AgentDayOneReport | null> =>
    apiClient
      .get<{ success: boolean; data: AgentDayOneReport | null }>('/agents/day-one-report', {
        params: { locationId },
      })
      .then((r) => r.data.data ?? null),

  // There is no hire call. Hiring costs real LLM budget and is an agency decision made in the command
  // center; the practice can only read whether an agent is assigned here.
  getAssignment: (locationId: string): Promise<AgentAssignment> =>
    apiClient
      .get<{
        success: boolean;
        data: AgentAssignment;
      }>('/agents/assignment', { params: { locationId } })
      .then((r) => r.data.data),

  /** Read-only mission review. This endpoint never starts a provider run or creates a task. */
  getMission: (locationId: string, roleKey?: string): Promise<AgentMissionSnapshot> =>
    apiClient
      .get<{
        success: boolean;
        data: AgentMissionSnapshot;
      }>('/agents/mission', { params: { locationId, roleKey } })
      .then((r) => r.data.data),

  approve: (taskId: string, locationId: string): Promise<void> =>
    apiClient.post(`/agents/tasks/${taskId}/approve`, { locationId }).then(() => undefined),

  // Skip an open schedule draft instead of rejecting it: no replacement is written.
  skip: (taskId: string, locationId: string, reason?: string): Promise<void> =>
    apiClient.post(`/agents/tasks/${taskId}/skip`, { locationId, reason }).then(() => undefined),

  // Cancel queued work before the agent starts it: nothing replaces it.
  cancel: (taskId: string, locationId: string, reason?: string): Promise<void> =>
    apiClient.post(`/agents/tasks/${taskId}/cancel`, { locationId, reason }).then(() => undefined),

  // Agency only: withdraw an open suggestion that is out of date or was done outside PracticeNova.
  withdraw: (
    taskId: string,
    locationId: string,
    kind: AgentTaskWithdrawalKind,
    reason?: string,
  ): Promise<void> =>
    apiClient
      .post(`/agents/tasks/${taskId}/withdraw`, { locationId, kind, reason })
      .then(() => undefined),

  // Send an approved Business Profile post to Google again after a failed publish.
  retryPublish: (taskId: string, locationId: string): Promise<void> =>
    apiClient.post(`/agents/tasks/${taskId}/publish`, { locationId }).then(() => undefined),

  // `reason` is stored on the cancelled task so the same work is not proposed again.
  reject: (taskId: string, locationId: string, reason?: string): Promise<void> =>
    apiClient.post(`/agents/tasks/${taskId}/reject`, { locationId, reason }).then(() => undefined),

  // "Mark reviewed" for a read-only audit, or for failed work — either way it moves to History. A neutral
  // acknowledge, so it carries no reason (unlike reject) and approves/publishes nothing.
  reviewTask: (taskId: string, locationId: string): Promise<void> =>
    apiClient.post(`/agents/tasks/${taskId}/review`, { locationId }).then(() => undefined),

  getThread: (locationId: string, taskId: string): Promise<AgentMessage[]> =>
    apiClient
      .get<{
        success: boolean;
        data: AgentMessage[];
      }>(`/agents/tasks/${taskId}/messages`, { params: { locationId } })
      .then((r) => r.data.data),

  sendMessage: (locationId: string, taskId: string, body: string): Promise<void> =>
    apiClient.post(`/agents/tasks/${taskId}/messages`, { locationId, body }).then(() => undefined),

  // After a reply the agent could not finish (the thread's notice), run it again on the same messages.
  retryReply: (locationId: string, taskId: string): Promise<void> =>
    apiClient.post(`/agents/tasks/${taskId}/retry-reply`, { locationId }).then(() => undefined),
  /** Stop an answer the agent is still working on: nothing more is sent. */
  stopReply: (locationId: string, taskId: string): Promise<void> =>
    apiClient.post(`/agents/tasks/${taskId}/stop-reply`, { locationId }).then(() => undefined),

  getVersions: (locationId: string, taskId: string): Promise<TaskVersions> =>
    apiClient
      .get<{
        success: boolean;
        data: TaskVersions;
      }>(`/agents/tasks/${taskId}/versions`, { params: { locationId } })
      .then((r) => r.data.data),

  restoreVersion: (locationId: string, taskId: string, n: number): Promise<void> =>
    apiClient.post(`/agents/tasks/${taskId}/restore`, { locationId, n }).then(() => undefined),

  // THE NOTEBOOK
  listFacts: (locationId: string): Promise<AgentFact[]> =>
    apiClient
      .get<{ success: boolean; data: AgentFact[] }>('/agents/facts', { params: { locationId } })
      .then((r) => r.data.data),

  editFact: (locationId: string, factId: string, text: string): Promise<void> =>
    apiClient.patch(`/agents/facts/${factId}`, { locationId, text }).then(() => undefined),

  retireFact: (locationId: string, factId: string): Promise<void> =>
    apiClient.delete(`/agents/facts/${factId}`, { params: { locationId } }).then(() => undefined),
};
